import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserPlus, FileUp, Trash2, X, Eye, Download, ReceiptText, XCircle, CircleDollarSign, Check, Send, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Admin — user provisioning + Titan Units assignment + document upload.
export default function AdminUsers() {
  const { api, token } = useAuth();
  const H = { Authorization: `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null); // user detail for docs

  const load = () => axios.get(`${api}/admin/users`, { headers: H }).then(r => setUsers(r.data.users)).catch(() => {});
  const [summary, setSummary] = useState(null);
  const loadSummary = () => axios.get(`${api}/admin/invoices/summary`, { headers: H }).then(r => setSummary(r.data)).catch(() => setSummary(null));
  useEffect(() => { load(); loadSummary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateUnits = async (uid, units) => {
    const n = Math.max(0, parseInt(units || "0", 10) || 0);
    await axios.patch(`${api}/admin/users/${uid}/units`, { titan_units: n }, { headers: H });
    toast.success("Titan Units updated.");
    load();
  };

  const deleteUser = async (uid) => {
    if (!window.confirm("Delete this account and archive its documents?")) return;
    await axios.delete(`${api}/admin/users/${uid}`, { headers: H });
    toast.success("Account removed.");
    load();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-muted-foreground">Admin</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tighter">Client accounts</h1>
          <p className="mt-3 text-[13px] text-muted-foreground max-w-xl">Provision new accounts, adjust their Titan Units allocation, and attach contracts or invoices.</p>
        </div>
        <button
          data-testid="admin-users-new-btn"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-[12px] tracking-widest uppercase"
        >
          <UserPlus className="w-4 h-4" /> New account
        </button>
      </div>

      {summary && (summary.count_unpaid + summary.count_paid) > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="admin-cashflow-summary">
          <div className="rounded-xl border border-border p-4 bg-card">
            <p className="overline text-muted-foreground">Paid</p>
            <p className="mt-1 font-display text-2xl text-emerald-400">
              ${summary.paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">{summary.count_paid} invoice{summary.count_paid > 1 ? "s" : ""}</p>
          </div>
          <div className="rounded-xl border border-border p-4 bg-card">
            <p className="overline text-muted-foreground">Outstanding</p>
            <p className="mt-1 font-display text-2xl text-amber-400">
              ${summary.unpaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">{summary.count_unpaid} invoice{summary.count_unpaid > 1 ? "s" : ""}</p>
          </div>
          <div className="rounded-xl border border-border p-4 bg-card">
            <p className="overline text-muted-foreground">Overdue</p>
            <p className={`mt-1 font-display text-2xl ${summary.overdue > 0 ? "text-rose-400" : "text-muted-foreground"}`}>
              ${summary.overdue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">{summary.count_overdue} invoice{summary.count_overdue > 1 ? "s" : ""}</p>
          </div>
        </div>
      )}

      <div className="mt-8 border border-border bg-card overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="admin-users-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Name", "Email", "Country", "Titan Units", "Documents", "Created", ""].map(h => (
                <th key={h} className="p-4 overline text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-border">
                <td className="p-4">{u.full_name}</td>
                <td className="p-4 font-mono text-[12px] text-muted-foreground">{u.email}</td>
                <td className="p-4">{u.country || "—"}</td>
                <td className="p-4">
                  <UnitsEditor initial={u.titan_units || 0} onSave={(v) => updateUnits(u.id, v)} testIdPrefix={u.id} />
                </td>
                <td className="p-4">
                  <button
                    data-testid={`admin-user-docs-${u.id}`}
                    onClick={() => setSelected(u)}
                    className="text-[11px] uppercase tracking-widest border border-border px-2 py-1 hover:bg-secondary"
                  >
                    {u.document_count || 0} · manage
                  </button>
                </td>
                <td className="p-4 font-mono text-[12px] text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  <button
                    data-testid={`admin-user-del-${u.id}`}
                    onClick={() => deleteUser(u.id)}
                    className="text-destructive/80 hover:text-destructive"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-[13px]">No client accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && <NewUserModal api={api} token={token} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {selected && <UserDocsModal user={selected} api={api} token={token} onClose={() => { setSelected(null); load(); loadSummary(); }} />}
    </motion.div>
  );
}

const UnitsEditor = ({ initial, onSave, testIdPrefix }) => {
  const [v, setV] = useState(initial);
  return (
    <div className="flex items-center gap-2">
      <input
        type="number" min="0" value={v} onChange={(e) => setV(e.target.value)}
        data-testid={`admin-user-units-input-${testIdPrefix}`}
        className="w-24 h-9 px-2 bg-transparent border border-border text-[13px] font-mono"
      />
      <button
        data-testid={`admin-user-units-save-${testIdPrefix}`}
        onClick={() => onSave(v)}
        className="text-[10px] uppercase tracking-widest border border-emerald-500/40 text-emerald-400 px-2 py-1 hover:bg-emerald-500/10"
      >Save</button>
    </div>
  );
};

const NewUserModal = ({ api, token, onClose, onDone }) => {
  const [form, setForm] = useState({ email: "", password: "", full_name: "", country: "", titan_units: 0, notes: "" });
  const [busy, setBusy] = useState(false);
  const u = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${api}/admin/users`, { ...form, titan_units: parseInt(form.titan_units || "0", 10) || 0 }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Account created.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Creation failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative w-full max-w-lg rounded-2xl border border-border bg-card p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-users-new-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        <p className="overline text-muted-foreground">Provision</p>
        <h2 className="mt-2 font-display text-2xl tracking-tighter">New client account</h2>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-new-user-form">
          <Field label="Full name"><input required data-testid="admin-new-name" value={form.full_name} onChange={u("full_name")} className="w-full h-11 bg-transparent border border-border px-3 text-[13px]" /></Field>
          <Field label="Email"><input required type="email" data-testid="admin-new-email" value={form.email} onChange={u("email")} className="w-full h-11 bg-transparent border border-border px-3 text-[13px] font-mono" /></Field>
          <Field label="Temporary password (min 8 chars)"><input required minLength={8} data-testid="admin-new-password" value={form.password} onChange={u("password")} className="w-full h-11 bg-transparent border border-border px-3 text-[13px] font-mono" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Country"><input data-testid="admin-new-country" value={form.country} onChange={u("country")} className="w-full h-11 bg-transparent border border-border px-3 text-[13px]" /></Field>
            <Field label="Titan Units"><input type="number" min="0" data-testid="admin-new-units" value={form.titan_units} onChange={u("titan_units")} className="w-full h-11 bg-transparent border border-border px-3 text-[13px] font-mono" /></Field>
          </div>
          <Field label="Notes (internal)"><textarea data-testid="admin-new-notes" value={form.notes} onChange={u("notes")} className="w-full h-20 bg-transparent border border-border p-3 text-[13px]" /></Field>
          <button disabled={busy} type="submit" data-testid="admin-new-submit" className="w-full h-11 rounded-none bg-emerald-500 hover:bg-emerald-400 text-white text-[12px] tracking-widest uppercase">{busy ? "…" : "Create account"}</button>
        </form>
      </div>
    </div>
  );
};

const UserDocsModal = ({ user, api, token, onClose }) => {
  const H = { Authorization: `Bearer ${token}` };
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ title: "", category: "contract", file: null });
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(null); // doc being rejected
  const [reasons, setReasons] = useState([]);
  const [editingInvoice, setEditingInvoice] = useState(null); // draft invoice being edited
  const [sending, setSending] = useState(null); // invoice being sent

  const load = () => axios.get(`${api}/admin/users/${user.id}`, { headers: H }).then(r => setDocs(r.data.documents)).catch(() => {});
  useEffect(() => {
    load();
    axios.get(`${api}/admin/rejection-reasons`, { headers: H })
      .then(r => setReasons(r.data.reasons || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const upload = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error("Choose a file.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("title", form.title);
      fd.append("category", form.category);
      await axios.post(`${api}/admin/users/${user.id}/documents`, fd, { headers: { ...H, "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded.");
      setForm({ title: "", category: "contract", file: null });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await axios.delete(`${api}/admin/documents/${id}`, { headers: H });
    load();
  };

  const verify = async (id) => {
    const r = await axios.patch(`${api}/admin/documents/${id}/verify`, {}, { headers: H });
    if (r.data?.kyc_completion_email_sent) {
      toast.success("Document verified — client KYC completion email sent.");
    } else {
      toast.success("Document marked verified.");
    }
    load();
  };

  const setInvoiceStatus = async (id, status) => {
    try {
      const r = await axios.patch(`${api}/admin/invoices/${id}/status`, { status }, { headers: H });
      if (status === "paid") {
        const rec = r.data?.receipt;
        if (rec?.receipt_number) {
          toast.success(`Invoice paid — receipt ${rec.receipt_number} ${rec.email_sent ? "sent to client" : "generated"}.`);
        } else {
          toast.success("Invoice marked as paid.");
        }
      } else {
        toast.success("Invoice marked as unpaid.");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  };

  // Fetch a document as a blob (admin can access via /me/documents/{id}/download)
  const fetchBlob = async (id) => {
    const r = await axios.get(`${api}/me/documents/${id}/download`, {
      headers: H,
      responseType: "blob",
    });
    return r.data;
  };

  const viewDoc = async (d) => {
    try {
      const blob = await fetchBlob(d.id);
      // Force the browser to render inline (PDF/image) regardless of Content-Disposition.
      const typed = new Blob([blob], { type: d.content_type || blob.type || "application/pdf" });
      const url = URL.createObjectURL(typed);
      window.open(url, "_blank", "noopener");
      // Give the tab time to load before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Unable to open document.");
    }
  };

  const downloadDoc = async (d) => {
    try {
      const blob = await fetchBlob(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || `${d.title || "document"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (e) {
      toast.error("Unable to download document.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-user-docs-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        <p className="overline text-muted-foreground">{user.full_name} · {user.email}</p>
        <h2 className="mt-2 font-display text-2xl tracking-tighter">Documents</h2>

        <form onSubmit={upload} className="mt-6 rounded-2xl border border-dashed border-border p-4 space-y-3" data-testid="admin-doc-upload-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Title"><input required data-testid="admin-doc-title" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className="w-full h-10 bg-transparent border border-border px-3 text-[13px]" /></Field>
            <Field label="Category">
              <select data-testid="admin-doc-category" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="w-full h-10 bg-transparent border border-border px-3 text-[13px]">
                <option value="contract">Contract</option>
                <option value="invoice">Invoice</option>
                <option value="statement">Statement</option>
              </select>
            </Field>
          </div>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="admin-doc-file" onChange={(e) => setForm(f => ({ ...f, file: e.target.files[0] }))} className="text-[12px] text-muted-foreground" />
          <button disabled={busy} type="submit" data-testid="admin-doc-upload" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] tracking-widest uppercase">
            <FileUp className="w-3.5 h-3.5" /> {busy ? "…" : "Upload"}
          </button>
        </form>

        <div className="mt-6 border border-border bg-background divide-y divide-border">
          {docs.map(d => {
            const isInvoice = d.category === "invoice" && d.invoice_meta;
            const meta = d.invoice_meta || {};
            const today = new Date().toISOString().slice(0, 10);
            const rawStatus = meta.status || (isInvoice ? "draft" : (d.verification_status || "pending"));
            const isOverdue = isInvoice && rawStatus !== "paid" && meta.due_date && meta.due_date < today;
            const displayStatus = isOverdue ? "overdue" : rawStatus;
            const statusClass =
              displayStatus === "paid"     ? "border-emerald-500/40 text-emerald-400"
            : displayStatus === "verified" ? "border-emerald-500/40 text-emerald-400"
            : displayStatus === "overdue"  ? "border-rose-500/40 text-rose-400"
            : displayStatus === "rejected" ? "border-rose-500/40 text-rose-400"
            : displayStatus === "sent"     ? "border-sky-500/40 text-sky-400"
            : "border-amber-500/40 text-amber-400";
            const rejectable = ["id_document", "residence_proof", "signed_contract", "icaf"].includes(d.category)
                              && d.verification_status !== "verified";
            return (
            <div key={d.id} data-testid={`admin-doc-row-${d.id}`} className="flex items-center justify-between p-4 gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display text-[14px] tracking-tight truncate">{d.title}</p>
                  <span
                    data-testid={`admin-doc-status-${d.id}`}
                    className={`text-[10px] tracking-widest uppercase px-2 py-0.5 border rounded-full ${statusClass}`}
                  >{displayStatus}</span>
                  {isInvoice && (
                    <span className="text-[10px] font-mono text-muted-foreground">
                      {meta.currency} {Number(meta.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · due {meta.due_date}
                    </span>
                  )}
                </div>
                <p className="text-[11px] tracking-widest uppercase text-muted-foreground mt-1">{d.category} · {new Date(d.created_at).toLocaleDateString()} · {Math.round((d.size || 0) / 1024)} kB</p>
              </div>
              <button
                onClick={() => viewDoc(d)}
                data-testid={`admin-doc-view-${d.id}`}
                title="View in new tab"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-sky-500/40 text-sky-400 px-2 py-1 hover:bg-sky-500/10"
              >
                <Eye className="w-3.5 h-3.5" /> View
              </button>
              <button
                onClick={() => downloadDoc(d)}
                data-testid={`admin-doc-download-${d.id}`}
                title="Download"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-border text-muted-foreground px-2 py-1 hover:bg-secondary"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </button>
              {isInvoice && rawStatus === "draft" && (
                <>
                  <button onClick={() => setEditingInvoice(d)} data-testid={`admin-invoice-edit-${d.id}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-border text-muted-foreground px-2 py-1 hover:bg-secondary">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => setSending(d)} data-testid={`admin-invoice-send-${d.id}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-sky-500/40 text-sky-400 px-2 py-1 hover:bg-sky-500/10">
                    <Send className="w-3.5 h-3.5" /> Send
                  </button>
                </>
              )}
              {isInvoice && rawStatus !== "draft" && rawStatus !== "paid" && (
                <button onClick={() => setInvoiceStatus(d.id, "paid")} data-testid={`admin-invoice-mark-paid-${d.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-emerald-500/40 text-emerald-400 px-2 py-1 hover:bg-emerald-500/10">
                  <CircleDollarSign className="w-3.5 h-3.5" /> Mark paid
                </button>
              )}
              {isInvoice && rawStatus === "paid" && (
                <button onClick={() => setInvoiceStatus(d.id, "unpaid")} data-testid={`admin-invoice-mark-unpaid-${d.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-amber-500/40 text-amber-400 px-2 py-1 hover:bg-amber-500/10">
                  Mark unpaid
                </button>
              )}
              {!isInvoice && d.verification_status !== "verified" && (
                <button onClick={() => verify(d.id)} data-testid={`admin-doc-verify-${d.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-emerald-500/40 text-emerald-400 px-2 py-1 hover:bg-emerald-500/10">
                  <Check className="w-3.5 h-3.5" /> Verify
                </button>
              )}
              {rejectable && (
                <button onClick={() => setRejecting(d)} data-testid={`admin-doc-reject-${d.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-rose-500/40 text-rose-400 px-2 py-1 hover:bg-rose-500/10">
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
              )}
              <button onClick={() => del(d.id)} data-testid={`admin-doc-del-${d.id}`} className="text-destructive/80 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            );
          })}
          {docs.length === 0 && <p className="p-6 text-center text-muted-foreground text-[13px]">No documents yet.</p>}
        </div>

        <InvoicePanel
          user={user} api={api} H={H}
          editing={editingInvoice}
          onGenerated={() => { setEditingInvoice(null); load(); }}
          onCancelEdit={() => setEditingInvoice(null)}
        />
      </div>

      {rejecting && (
        <RejectModal
          doc={rejecting}
          api={api}
          H={H}
          reasons={reasons}
          onClose={() => setRejecting(null)}
          onDone={() => { setRejecting(null); load(); }}
        />
      )}
      {sending && (
        <SendInvoiceModal
          doc={sending}
          api={api}
          H={H}
          clientEmail={user.email}
          onClose={() => setSending(null)}
          onDone={() => { setSending(null); load(); }}
        />
      )}
    </div>
  );
};

const RejectModal = ({ doc, api, H, reasons, onClose, onDone }) => {
  const [code, setCode] = useState(reasons[0]?.code || "illegible");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const needsDetails = code === "other";

  const submit = async (e) => {
    e.preventDefault();
    if (needsDetails && !details.trim()) return toast.error("Please add the details.");
    setBusy(true);
    try {
      const r = await axios.patch(
        `${api}/admin/documents/${doc.id}/reject`,
        { reason_code: code, details: details.trim() },
        { headers: H },
      );
      if (r.data?.email_sent) toast.success("Document rejected — client notified by email.");
      else toast.success("Document rejected (email not sent).");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rejection failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="relative w-full max-w-md rounded-2xl border border-rose-500/30 bg-card p-6 md:p-7"
        data-testid="admin-doc-reject-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        <p className="overline text-rose-400">Reject document</p>
        <h3 className="mt-2 font-display text-xl tracking-tighter">{doc.title}</h3>
        <p className="text-[12px] text-muted-foreground mt-1">
          The document will be removed from the client&apos;s portal and they will receive an email with the reason and a re-upload link.
        </p>

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="overline text-muted-foreground block mb-1.5">Reason</span>
            <select data-testid="admin-doc-reject-reason" value={code} onChange={(e) => setCode(e.target.value)}
              className="w-full h-10 bg-transparent border border-border px-3 text-[13px]">
              {reasons.map(r => (<option key={r.code} value={r.code}>{r.label}</option>))}
            </select>
          </label>
          <label className="block">
            <span className="overline text-muted-foreground block mb-1.5">
              Additional details {needsDetails && <span className="text-rose-400">(required)</span>}
            </span>
            <textarea data-testid="admin-doc-reject-details" value={details} onChange={(e) => setDetails(e.target.value)}
              placeholder="Optional message to the client, in their own language if possible."
              className="w-full h-24 bg-transparent border border-border p-3 text-[13px]" />
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 border border-border text-[11px] tracking-widest uppercase text-muted-foreground hover:bg-secondary">
            Cancel
          </button>
          <button type="submit" disabled={busy} data-testid="admin-doc-reject-submit"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-rose-500 hover:bg-rose-400 text-white text-[11px] tracking-widest uppercase disabled:opacity-60">
            <XCircle className="w-3.5 h-3.5" /> {busy ? "Rejecting…" : "Reject & email client"}
          </button>
        </div>
      </form>
    </div>
  );
};

const DEFAULT_BANK_INSTRUCTIONS = `Beneficiary: Strategy Intelligence Solutions LTD
Bank:
IBAN:
BIC / SWIFT:
Reference: <invoice number>
Please reference the invoice number on every transfer.`;

const InvoicePanel = ({ user, api, H, editing, onGenerated, onCancelEdit }) => {
  const today = new Date().toISOString().slice(0, 10);
  const in14 = new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    units: user.titan_units || 250,
    unit_price_usd: 40,
    currency: "USD",
    issue_date: today,
    due_date: in14,
    tax_rate: 0,
    description: "",
    notes: "",
    bank_instructions: DEFAULT_BANK_INSTRUCTIONS,
  });

  const isEditing = !!editing;

  // Whenever a draft is selected for editing, pre-fill the form and open it
  useEffect(() => {
    if (!editing) return;
    const m = editing.invoice_meta || {};
    setForm({
      units: m.units ?? 250,
      unit_price_usd: m.unit_price_usd ?? 40,
      currency: m.currency ?? "USD",
      issue_date: m.issue_date ?? today,
      due_date: m.due_date ?? in14,
      tax_rate: m.tax_rate ?? 0,
      description: m.description ?? "",
      notes: m.notes ?? "",
      bank_instructions: m.bank_instructions || DEFAULT_BANK_INSTRUCTIONS,
    });
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const subtotal = (parseFloat(form.units) || 0) * (parseFloat(form.unit_price_usd) || 0);
  const tax = subtotal * ((parseFloat(form.tax_rate) || 0) / 100);
  const total = subtotal + tax;
  const money = (v) => `${(form.currency || "USD")} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const closeForm = () => {
    setOpen(false);
    onCancelEdit?.();
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${api}/admin/invoice-template.pdf`, { headers: H, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Titan-Infra-Invoice-Template.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch { toast.error("Unable to download template."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        units: parseInt(form.units || "0", 10) || 0,
        unit_price_usd: parseFloat(form.unit_price_usd) || 0,
        tax_rate: parseFloat(form.tax_rate) || 0,
      };
      if (isEditing) {
        const r = await axios.put(`${api}/admin/invoices/${editing.id}`, body, { headers: H });
        toast.success(`Draft ${r.data.invoice_number} updated.`);
      } else {
        const r = await axios.post(`${api}/admin/users/${user.id}/invoice`, body, { headers: H });
        toast.success(`Draft ${r.data.invoice_number} saved — review, then Send to client.`);
      }
      closeForm();
      onGenerated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-8" data-testid="admin-invoice-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-muted-foreground">Invoicing</p>
          <h3 className="mt-1 font-display text-xl tracking-tighter">{isEditing ? `Edit draft ${editing.invoice_number}` : "Generate an invoice"}</h3>
          <p className="text-[12px] text-muted-foreground mt-1">
            Save as draft first, edit anything (including bank details), then send to the client with an optional personal note.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadTemplate}
            data-testid="admin-invoice-template-download"
            className="inline-flex items-center gap-2 h-10 px-4 border border-border text-[11px] tracking-widest uppercase hover:bg-secondary"
          >
            <Download className="w-3.5 h-3.5" /> Blank template
          </button>
          {!isEditing && (
            <button
              onClick={() => setOpen(v => !v)}
              data-testid="admin-invoice-new-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-[11px] tracking-widest uppercase"
            >
              <ReceiptText className="w-3.5 h-3.5" /> {open ? "Close" : "New invoice"}
            </button>
          )}
          {isEditing && (
            <button
              onClick={closeForm}
              data-testid="admin-invoice-cancel-edit"
              className="inline-flex items-center gap-2 h-10 px-4 border border-border text-[11px] tracking-widest uppercase text-muted-foreground hover:bg-secondary"
            >
              <X className="w-3.5 h-3.5" /> Cancel edit
            </button>
          )}
        </div>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 rounded-2xl border border-dashed border-border p-4 space-y-4" data-testid="admin-invoice-form">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Units">
              <input required type="number" min="1" data-testid="admin-invoice-units" value={form.units}
                onChange={(e) => setForm(f => ({ ...f, units: e.target.value }))}
                className="w-full h-10 bg-transparent border border-border px-3 text-[13px] font-mono" />
            </Field>
            <Field label="Unit price">
              <input required type="number" min="0" step="0.01" data-testid="admin-invoice-unit-price" value={form.unit_price_usd}
                onChange={(e) => setForm(f => ({ ...f, unit_price_usd: e.target.value }))}
                className="w-full h-10 bg-transparent border border-border px-3 text-[13px] font-mono" />
            </Field>
            <Field label="Currency">
              <select data-testid="admin-invoice-currency" value={form.currency}
                onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))}
                className="w-full h-10 bg-transparent border border-border px-3 text-[13px]">
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </Field>
            <Field label="Issue date">
              <input required type="date" data-testid="admin-invoice-issue-date" value={form.issue_date}
                onChange={(e) => setForm(f => ({ ...f, issue_date: e.target.value }))}
                className="w-full h-10 bg-transparent border border-border px-3 text-[13px] font-mono" />
            </Field>
            <Field label="Due date">
              <input required type="date" data-testid="admin-invoice-due-date" value={form.due_date}
                onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))}
                className="w-full h-10 bg-transparent border border-border px-3 text-[13px] font-mono" />
            </Field>
            <Field label="Tax rate (%)">
              <input type="number" min="0" step="0.1" data-testid="admin-invoice-tax" value={form.tax_rate}
                onChange={(e) => setForm(f => ({ ...f, tax_rate: e.target.value }))}
                className="w-full h-10 bg-transparent border border-border px-3 text-[13px] font-mono" />
            </Field>
          </div>
          <Field label="Description (optional)">
            <input data-testid="admin-invoice-description" value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Subscription to Titan Infra — Titan Units (AI compute infrastructure at cost)."
              className="w-full h-10 bg-transparent border border-border px-3 text-[13px]" />
          </Field>
          <Field label="Bank / payment instructions (printed as-is in the PDF)">
            <textarea data-testid="admin-invoice-bank" value={form.bank_instructions}
              onChange={(e) => setForm(f => ({ ...f, bank_instructions: e.target.value }))}
              className="w-full h-36 bg-transparent border border-border p-3 text-[12px] font-mono leading-relaxed" />
            <p className="text-[11px] text-muted-foreground mt-1">One line per row. These lines appear verbatim in the invoice PDF.</p>
          </Field>
          <Field label="Notes (optional)">
            <textarea data-testid="admin-invoice-notes" value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full h-20 bg-transparent border border-border p-3 text-[13px]" />
          </Field>

          <div className="rounded-xl border border-border p-3 bg-background/50">
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-muted-foreground">Subtotal</span><span className="font-mono">{money(subtotal)}</span>
            </div>
            {parseFloat(form.tax_rate) > 0 && (
              <div className="flex items-center justify-between text-[12px] mt-1">
                <span className="text-muted-foreground">Tax ({form.tax_rate}%)</span><span className="font-mono">{money(tax)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-[14px] mt-2 pt-2 border-t border-border">
              <span className="font-display tracking-tight">Total due</span>
              <span className="font-mono text-emerald-400" data-testid="admin-invoice-total">{money(total)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button disabled={busy} type="submit" data-testid="admin-invoice-submit"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-[11px] tracking-widest uppercase disabled:opacity-60">
              <ReceiptText className="w-3.5 h-3.5" /> {busy ? "Saving…" : (isEditing ? "Update draft" : "Save as draft")}
            </button>
            <button type="button" onClick={closeForm}
              className="inline-flex items-center gap-2 h-11 px-4 border border-border text-[11px] tracking-widest uppercase text-muted-foreground hover:bg-secondary">
              Cancel
            </button>
            <span className="text-[11px] text-muted-foreground ml-2">No email is sent at this step. Use the <em>Send</em> button on the draft when it is ready.</span>
          </div>
        </form>
      )}
    </div>
  );
};

const SendInvoiceModal = ({ doc, api, H, clientEmail, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = doc.invoice_meta || {};

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(
        `${api}/admin/invoices/${doc.id}/send`,
        { personal_note: note.trim() || null },
        { headers: H },
      );
      if (r.data?.email_sent) toast.success(`Invoice sent to ${clientEmail}.`);
      else toast.warning("Invoice status updated but email delivery failed.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sending failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="relative w-full max-w-lg rounded-2xl border border-sky-500/30 bg-card p-6 md:p-7"
        data-testid="admin-invoice-send-modal">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        <p className="overline text-sky-400">Send invoice</p>
        <h3 className="mt-2 font-display text-xl tracking-tighter">{doc.invoice_number}</h3>
        <p className="text-[12px] text-muted-foreground mt-1">
          To <strong className="text-foreground">{clientEmail}</strong> — {meta.currency} {Number(meta.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · due {meta.due_date}
        </p>

        <div className="mt-5">
          <label className="block">
            <span className="overline text-muted-foreground block mb-1.5">Personal note (optional)</span>
            <textarea data-testid="admin-invoice-send-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Merci pour votre confiance — n'hésitez pas à me contacter directement si vous avez la moindre question."
              className="w-full h-28 bg-transparent border border-border p-3 text-[13px]" />
          </label>
          <p className="text-[11px] text-muted-foreground mt-1.5">If provided, this note appears in a highlighted block at the top of the client email, before the invoice summary.</p>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 border border-border text-[11px] tracking-widest uppercase text-muted-foreground hover:bg-secondary">
            Cancel
          </button>
          <button type="submit" disabled={busy} data-testid="admin-invoice-send-submit"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-[11px] tracking-widest uppercase disabled:opacity-60">
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send to client"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline text-muted-foreground block mb-1.5">{label}</span>
    {children}
  </label>
);
